import { cn } from "@/lib/utils";

interface MovaLogoProps {
  variant?: "full" | "compact" | "icon" | "white";
  showTagline?: boolean;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

/**
 * Official MOVA MARKET ETH brand logo component
 * Faithfully matches the official sticker visual identity:
 * - Dynamic shopping bag with upward curving arrow in teal & ocean blue (#00B7B0, #087DB5)
 * - Vibrant orange accent swoosh (#FF8A00)
 * - Deep navy brand typography (#073B70)
 * - Official tagline: "We Don't Just Sell, We Guarantee Quality!"
 */
export function MovaLogo({
  variant = "full",
  showTagline = false,
  className = "",
  size = "md"
}: MovaLogoProps) {
  const isWhite = variant === "white";

  const sizeClasses = {
    sm: "h-7",
    md: "h-9 sm:h-10",
    lg: "h-12",
    xl: "h-16"
  }[size];

  const iconSizes = {
    sm: "w-7 h-7",
    md: "w-9 h-9 sm:w-10 sm:h-10",
    lg: "w-12 h-12",
    xl: "w-16 h-16"
  }[size];

  return (
    <div className={cn("inline-flex items-center gap-2.5 select-none", className)}>
      {/* Vector Bag & Swoosh Icon */}
      <svg
        viewBox="0 0 120 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn("shrink-0", iconSizes)}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="movaBagArrow" x1="10" y1="90" x2="110" y2="10" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#073B70" />
            <stop offset="45%" stopColor="#087DB5" />
            <stop offset="100%" stopColor="#00B7B0" />
          </linearGradient>
          <linearGradient id="movaOrangeAccent" x1="20" y1="60" x2="80" y2="95" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#FF9F1C" />
            <stop offset="100%" stopColor="#FF8A00" />
          </linearGradient>
          <linearGradient id="movaBagBody" x1="30" y1="20" x2="85" y2="80" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#087DB5" />
            <stop offset="100%" stopColor="#073B70" />
          </linearGradient>
        </defs>

        {/* Orange Base Swoosh */}
        <path
          d="M 32 68 C 24 74 22 84 38 90 C 52 94 72 88 84 76 C 76 82 58 86 44 82 C 34 80 30 74 32 68 Z"
          fill="url(#movaOrangeAccent)"
        />

        {/* Bag Handle */}
        <path
          d="M 46 25 C 46 16 52 10 62 10 C 72 10 78 16 78 25"
          stroke={isWhite ? "#FFFFFF" : "#087DB5"}
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />

        {/* Shopping Bag Outline / Body */}
        <rect
          x="34"
          y="25"
          width="54"
          height="52"
          rx="12"
          stroke={isWhite ? "#FFFFFF" : "url(#movaBagBody)"}
          strokeWidth="6.5"
          fill="none"
        />

        {/* Upper Dynamic Curved Swoosh with Arrow */}
        <path
          d="M 12 55 C 10 78 26 92 52 92 C 78 92 100 74 106 44"
          stroke="url(#movaBagArrow)"
          strokeWidth="7.5"
          strokeLinecap="round"
          fill="none"
        />

        {/* Dynamic Arrowhead */}
        <polygon
          points="106,20 118,44 94,40"
          fill="#00B7B0"
        />
      </svg>

      {/* Typography */}
      {variant !== "icon" && (
        <div className="flex flex-col justify-center leading-none">
          <div className="flex items-center gap-1.5 font-bold tracking-tight">
            <span
              className={cn(
                "text-lg sm:text-2xl font-bold tracking-tight transition-colors",
                isWhite ? "text-white" : "text-[#073B70] dark:text-white"
              )}
            >
              MOVA MARKET
            </span>
            <span
              className={cn(
                "rounded px-1.5 py-0.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider",
                isWhite
                  ? "bg-white/20 text-white"
                  : "bg-[#087DB5]/10 text-[#087DB5] dark:bg-white/10 dark:text-teal-400"
              )}
            >
              ETH
            </span>
          </div>

          {(showTagline || variant === "full") && (
            <span
              className={cn(
                "mt-0.5 text-[9.5px] sm:text-[11px] font-semibold tracking-tight",
                isWhite ? "text-slate-200" : "text-[#087DB5] dark:text-teal-400"
              )}
            >
              We Don&apos;t Just Sell, We Guarantee Quality!
            </span>
          )}
        </div>
      )}
    </div>
  );
}
